import { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Heart, Send } from "lucide-react";
import Avatar from "@/components/Avatar";

export const NOTE_STYLES = {
  sunrise: { label: "Sunrise", bg: "bg-[#FFF3E8]", border: "border-[#F3C9B1]", accent: "text-[#C25832]" },
  meadow: { label: "Meadow", bg: "bg-[#E8F4F1]", border: "border-[#B5DBD1]", accent: "text-[#1F5F4F]" },
  lavender: { label: "Lavender", bg: "bg-[#F1ECFA]", border: "border-[#CFC2EA]", accent: "text-[#5B3FA0]" },
};

export function ThankYouCard({ note, compact = false }) {
  const s = NOTE_STYLES[note.style] || NOTE_STYLES.sunrise;
  return (
    <div data-testid={`thank-you-card-${note.id}`} className={`${s.bg} ${s.border} border-2 rounded-2xl ${compact ? "p-4" : "p-6"} relative`}>
      <Heart className={`w-6 h-6 ${s.accent} absolute top-4 right-4`} fill="currentColor" />
      <p className={`font-display italic text-slate-800 ${compact ? "text-lg" : "text-xl"} leading-relaxed pr-8`}>“{note.message}”</p>
      <div className="flex items-center gap-3 mt-4">
        <Avatar user={{ name: note.seeker_name, avatar: note.seeker_avatar }} size="sm" color="bg-[#D96B43]" />
        <div>
          <p className="font-semibold text-slate-900">— {note.seeker_name}</p>
          <p className="text-sm text-slate-600">{note.request_title} · {new Date(note.created_at).toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}

export default function ThankYouComposer({ requestId, helperName, onSent }) {
  const [message, setMessage] = useState("");
  const [style, setStyle] = useState("sunrise");
  const [busy, setBusy] = useState(false);
  const s = NOTE_STYLES[style];

  const send = async () => {
    if (!message.trim()) { toast.error("Please write a few kind words first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/thankyou", { request_id: requestId, message, style });
      toast.success(data.email_status === "sent" ? `Your card was sent to ${helperName} by email too!` : "Your thank-you card is on their profile!");
      onSent(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="thank-you-composer" className="mt-8 pt-6 border-t border-[#E2D9CE]">
      <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Heart className="w-5 h-5 text-[#D96B43]" fill="#D96B43" /> Send {helperName} a thank-you card</h3>
      <p className="text-slate-600 mt-1">It will appear on their profile and be emailed to them.</p>
      <div className="flex gap-2 mt-4">
        {Object.entries(NOTE_STYLES).map(([k, v]) => (
          <button key={k} type="button" data-testid={`note-style-${k}`} onClick={() => setStyle(k)}
            className={`${v.bg} ${v.border} border-2 rounded-xl px-4 min-h-[44px] font-semibold ${style === k ? "ring-2 ring-offset-2 ring-[#D96B43]" : ""}`}>{v.label}</button>
        ))}
      </div>
      <div className={`${s.bg} ${s.border} border-2 rounded-2xl p-4 mt-4`}>
        <textarea data-testid="thank-you-message" rows={3} maxLength={600} value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder={`Dear ${helperName}, thank you so much for…`}
          className="w-full bg-transparent font-display italic text-xl text-slate-800 outline-none resize-none placeholder:text-slate-400" />
      </div>
      <button data-testid="send-thank-you-btn" onClick={send} disabled={busy}
        className="mt-3 flex items-center gap-2 bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold px-6 min-h-[48px] rounded-xl transition-colors">
        <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send thank-you card"}
      </button>
    </div>
  );
}
